// Bottom sheet. Used for a moment preview on the map and for the caption
// step in Capture. Flat surface, hairline top border, no shadow halo.
import { useEffect, type ReactNode } from "react";
import { Icon, IconClose } from "@traveldiary/ui";
import { useApp } from "../state/app";

export interface SheetProps {
  open: boolean;
  title: string;
  onClose(): void;
  children: ReactNode;
  footer?: ReactNode;
}

export function Sheet({ open, title, onClose, children, footer }: SheetProps) {
  const { t } = useApp();

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    globalThis.addEventListener?.("keydown", onKey);
    return () => globalThis.removeEventListener?.("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="sheet-layer">
      <button type="button" className="sheet-scrim" aria-label={t("common.close")} onClick={onClose} />
      <section className="sheet" role="dialog" aria-modal="true" aria-label={title}>
        <div className="sheet-head">
          <h2 className="sheet-title">{title}</h2>
          <button type="button" className="btn-icon" onClick={onClose} aria-label={t("common.close")}>
            <Icon size={20}>
              <IconClose />
            </Icon>
          </button>
        </div>
        <div className="sheet-body">{children}</div>
        {footer ? <div className="sheet-foot">{footer}</div> : null}
      </section>
    </div>
  );
}
